import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input, type InputProps } from '@/components/ui/Input'

export interface FormInputProps<TFieldValues extends FieldValues>
  extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'errorMessage'> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
}

export function FormInput<TFieldValues extends FieldValues>({
  control,
  name,
  ...props
}: FormInputProps<TFieldValues>) {
  const { t } = useTranslation()
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  const isNumber = props.type === 'number'

  const handleChange = (value: string) => {
    if (isNumber) {
      // Native number inputs still emit strings; coerce to a number so
      // number-based Zod schemas validate correctly. Empty (or an
      // unparseable intermediate state like "e") becomes null.
      if (value === '') {
        field.onChange(null)
      } else {
        const parsed = Number(value)
        field.onChange(Number.isNaN(parsed) ? null : parsed)
      }
    } else {
      field.onChange(value)
    }
  }

  return (
    <Input
      {...props}
      value={field.value == null ? '' : String(field.value)}
      onChange={handleChange}
      onBlur={field.onBlur}
      errorMessage={error?.message ? t(error.message) : undefined}
      isInvalid={!!error}
    />
  )
}

export default FormInput
